import { Link } from 'react-router-dom';
import { Leaf, MapPin, ShoppingBasket, Timer } from 'lucide-react';
import { ROUTE_NAMES, APP_NAME } from '@tsgecart/config';
import { Seo } from '../components/seo.js';
import { TrustBadgesSection } from '../features/storefront/home/components/trust-badges-section.js';

const VALUES = [
  {
    icon: Timer,
    title: 'Speed you can plan around',
    description:
      'We built our own local warehouse and delivery network so an order for tonight’s dinner actually shows up in time for it.',
  },
  {
    icon: Leaf,
    title: 'Freshness first',
    description:
      'Produce and daily essentials are sourced and restocked frequently rather than sitting on a shelf, so what arrives is what you’d pick yourself.',
  },
  {
    icon: ShoppingBasket,
    title: 'Everyday essentials, one basket',
    description:
      'Fruits and vegetables, staples, dairy, snacks and household items together in a single order -- no juggling multiple apps for one grocery run.',
  },
  {
    icon: MapPin,
    title: 'Built for your neighbourhood',
    description:
      'Delivery windows and product availability are tuned to the areas we actually serve, not a generic one-size-fits-all catalog.',
  },
] as const;

/**
 * Static marketing page -- no catalog/order data fetched here, same
 * "clearly non-catalog copy" rule the homepage's trust badges follow.
 * Deliberately avoids fabricated stats/testimonials/review counts (this
 * codebase's "no fake data" rule, see docs/storefront-seo.md and the
 * home page's own doc comment).
 */
export function AboutPage() {
  return (
    <>
      <Seo
        title="About us"
        description={`Learn what ${APP_NAME} is building: fast, reliable grocery delivery with fresh produce and everyday essentials.`}
        canonicalPath={ROUTE_NAMES.about}
        ogType="website"
      />

      <section className="mx-auto max-w-4xl px-4 py-12 text-center sm:px-8 sm:py-16">
        <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl">About {APP_NAME}</h1>
        <p className="mx-auto mt-4 max-w-2xl text-base text-gray-600">
          {APP_NAME} is a quick-commerce grocery platform: fresh produce, daily staples and household essentials,
          delivered from a local warehouse near you. We started with a simple frustration -- grocery shopping
          shouldn&apos;t eat an hour of your day -- and built the ordering, inventory and delivery experience around
          fixing exactly that.
        </p>
        <div className="mt-8">
          <Link
            to={ROUTE_NAMES.shop}
            className="focus-visible:ring-brand bg-brand text-brand-foreground inline-block rounded-full px-6 py-3 text-sm font-semibold hover:opacity-90 focus-visible:outline-none focus-visible:ring-2"
          >
            Start shopping
          </Link>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-4 sm:px-8">
        <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Why shop with us</h2>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {VALUES.map((value) => (
            <div key={value.title} className="flex flex-col items-start gap-3 rounded-2xl border border-gray-100 bg-white p-5">
              <span className="bg-brand/10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full">
                <value.icon className="text-brand-foreground h-5 w-5" aria-hidden="true" />
              </span>
              <p className="text-base font-semibold text-gray-900">{value.title}</p>
              <p className="text-sm text-gray-500">{value.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-10 sm:px-8">
        <div className="rounded-2xl bg-gray-900 px-6 py-8 text-center text-white sm:px-10 sm:py-10">
          <h2 className="text-xl font-bold sm:text-2xl">Have a question before you order?</h2>
          <p className="mt-2 text-sm text-gray-300">
            Our support team is happy to help with anything from delivery areas to a specific order.
          </p>
          <Link
            to={ROUTE_NAMES.contact}
            className="focus-visible:ring-brand mt-5 inline-block rounded-full border border-white/30 px-5 py-2 text-sm font-semibold text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2"
          >
            Contact us
          </Link>
        </div>
      </section>

      <TrustBadgesSection />
    </>
  );
}
